import React, { useState, useEffect, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useNotify } from "../../../context/NotificationContext";
import ReactQuillEditor from "../../../components/ReactQuillEditor";
import { Card, Form, Input, Select, Button, Typography, Spin, Space, InputNumber, Row, Col, Badge } from "antd";
import {
  SaveOutlined,
  ReloadOutlined,
  ArrowLeftOutlined,
  QuestionCircleOutlined,
  DeleteOutlined,
} from "@ant-design/icons";
import masterService from "../../../services/masterService";
import { useCommon } from "../../../context/CommonContext";

const { Title } = Typography;
const { Option } = Select;

const EditFaq = () => {
  const { faq_id } = useParams();
  const navigate = useNavigate();
  const { setPageTitle } = useCommon();
  const [form] = Form.useForm();
  const notify = useNotify();
  const [loading, setLoading] = useState(false);
  const [loadingData, setLoadingData] = useState(false);
  const [languages, setLanguages] = useState([]);

  const fetchLanguages = async () => {
    const response = await masterService.getVideoLanguages({
      isAll: true,
      status: "Y",
    });
    const data = response?.data?.data || response?.data || [];
    const list = data.map((item) => ({
      value: item.lang_id,
      label: item.lang_name,
    }));
    setLanguages(list);
  };

  const fetchFaqDetails = useCallback(async () => {
    if (!faq_id) {
      notify.error({ message: "FAQ ID is required" });
      navigate("/faqs");
      return;
    }

    setLoadingData(true);
    try {
      const response = await masterService.getSingleFaq(faq_id);

      if (response.data && response.data.status) {
        const faqData = response.data.data[0] || response.data.data;
        if (faqData && faqData.faq_question) {
          form.setFieldsValue({
            faq_order: faqData.faq_order,
            faq_question: faqData.faq_question,
            faq_answer: faqData.faq_answer,
            is_active: faqData.is_active || "Y",
            multilingual_data: faqData.multilingual_data,
          });
        } else {
          notify.error({ message: "FAQ data not found" });
          navigate("/faqs");
        }
      } else {
        notify.error({
          message: response.data?.message || "Failed to fetch FAQ details",
        });
        navigate("/faqs");
      }
    } catch (error) {
      console.error("Error fetching FAQ details:", error);
      notify.error({ message: "Error fetching FAQ details" });
      navigate("/faqs");
    } finally {
      setLoadingData(false);
    }
  }, [faq_id, form, navigate]);

  useEffect(() => {
    setPageTitle("Edit FAQ");
    fetchFaqDetails();
    fetchLanguages();
  }, [fetchFaqDetails, setPageTitle]);

  const handleSubmit = async (values) => {
    setLoading(true);
    try {
      const payload = {
        ...values,
        faq_id: faq_id,
      };

      const response = await masterService.updateFaq(faq_id, payload);

      if (response.data && response.data.status) {
        notify.success({
          message: response.data.message || "FAQ updated successfully",
        });
        navigate("/faqs");
      } else {
        notify.error({
          message: response.data.message || "Failed to update FAQ",
        });
      }
    } catch (error) {
      console.error("Error updating FAQ:", error);
      notify.error({ message: error?.data?.message || "Error updating FAQ" });
    } finally {
      setLoading(false);
    }
  };

  const handleReset = () => {
    form.resetFields();
  };

  const handleGoBack = () => {
    navigate("/faqs");
  };

  return (
    <div className="min-h-screen">
      <Spin spinning={loadingData}>
        <Card
        >
          <Form
            form={form}
            layout="vertical"
            size="large"
            onFinish={handleSubmit}
            className="space-y-6"
          >
            {/* FAQ Information Section */}
            <div
              className="bg-white p-6 rounded-lg border border-gray-200"
              style={{ backgroundColor: "#ffffff", borderColor: "#d9d9d9" }}
            >
              <div className="flex items-center gap-2 mb-4">
                <QuestionCircleOutlined />
                <Title
                  level={4}
                  className="!mb-0"
                  style={{ color: "rgba(0, 0, 0, 0.88)" }}
                >
                  FAQ Information
                </Title>
              </div>

              <Row gutter={[24, 16]}>
                <Col xs={24} sm={12} lg={8}>
                  <Form.Item
                    label={
                      <span
                        className="font-semibold"
                        style={{ color: "rgba(0, 0, 0, 0.88)" }}
                      >
                        Order
                      </span>
                    }
                    name="faq_order"
                    rules={[
                      { required: true, message: "Please enter FAQ order" },
                    ]}
                  >
                    <InputNumber
                      placeholder="Enter FAQ order"
                      style={{ width: "100%" }}
                      min={1}
                    />
                  </Form.Item>
                </Col>

                <Col xs={24} sm={12} lg={8}>
                  <Form.Item
                    label={
                      <span
                        className="font-semibold"
                        style={{ color: "rgba(0, 0, 0, 0.88)" }}
                      >
                        Status
                      </span>
                    }
                    name="is_active"
                  >
                    <Select className="rounded-lg">
                      <Option value="Y">
                        <Badge status="success" text="Active" />
                      </Option>
                      <Option value="N">
                        <Badge status="default" text="Inactive" />
                      </Option>
                    </Select>
                  </Form.Item>
                </Col>
              </Row>

              <Row gutter={[24, 16]}>
                <Col span={24}>
                  <Form.Item
                    label={
                      <span
                        className="font-semibold"
                        style={{ color: "rgba(0, 0, 0, 0.88)" }}
                      >
                        Question
                      </span>
                    }
                    name="faq_question"
                    rules={[
                      { required: true, message: "Please enter question" },
                    ]}
                  >
                    <Input
                      placeholder="Enter question"
                      maxLength={255}
                      showCount
                      className="rounded-lg"
                    />
                  </Form.Item>
                </Col>
              </Row>

              <Row gutter={[24, 16]}>
                <Col span={24}>
                  <Form.Item
                    label={
                      <span
                        className="font-semibold"
                        style={{ color: "rgba(0, 0, 0, 0.88)" }}
                      >
                        Answer
                      </span>
                    }
                    name="faq_answer"
                    rules={[{ required: true, message: "Please enter answer" }]}
                  >
                    {/* <TextArea
                      placeholder="Enter answer"
                      rows={6}
                      showCount
                      className="rounded-lg"
                    /> */}
                    <ReactQuillEditor
                      placeholder="Enter answer"
                      Height={400}
                    />
                  </Form.Item>
                </Col>
              </Row>
            </div>

            {/* Multilingual Data Section */}
            <div
              className="bg-white p-6 rounded-lg border border-gray-200"
              style={{ backgroundColor: "#ffffff", borderColor: "#d9d9d9" }}
            >
              <div className="flex items-center gap-2 mb-4">
                <QuestionCircleOutlined />
                <Title
                  level={4}
                  className="!mb-0"
                  style={{ color: "rgba(0, 0, 0, 0.88)" }}
                >
                  Multilingual Data
                </Title>
              </div>

              <Form.List name="multilingual_data">
                {(fields, { add, remove }) => (
                  <>
                    {fields.map(({ key, name, ...restField }, index) => (
                      <div key={key} className="mb-4 p-4 rounded-lg border">
                        <div className="flex justify-between items-center mb-4">
                          <span
                            className="font-semibold"
                            style={{ color: "rgba(0, 0, 0, 0.88)" }}
                          >
                            Language Entry #{index + 1}
                          </span>
                          <Button
                            type="text"
                            danger
                            size="small"
                            onClick={() => remove(name)}
                            icon={<DeleteOutlined />}
                            style={{ fontWeight: 600 }}
                          >
                            Remove
                          </Button>
                        </div>

                        <div className="grid grid-cols-[200px_1fr] gap-4">
                          <Form.Item
                            label={
                              <span
                                className="font-semibold"
                                style={{ color: "rgba(0, 0, 0, 0.88)" }}
                              >
                                Language
                              </span>
                            }
                            name={[name, "lang_id"]}
                            rules={[
                              {
                                required: true,
                                message: "Please select language",
                              },
                            ]}
                          >
                            <Select
                              placeholder="Select language"
                              options={languages}
                              className="rounded-lg"
                              showSearch
                              optionFilterProp="label"
                            />
                          </Form.Item>

                          <Form.Item
                            {...restField}
                            name={[name, "faq_question"]}
                            label={
                              <span
                                className="font-semibold"
                                style={{ color: "rgba(0, 0, 0, 0.88)" }}
                              >
                                Question
                              </span>
                            }
                            rules={[
                              {
                                required: true,
                                message: "Please enter question",
                              },
                            ]}
                          >
                            <Input
                              placeholder="Enter question in this language"
                              maxLength={255}
                              showCount
                              className="rounded-lg"
                            />
                          </Form.Item>
                        </div>

                        <Form.Item
                          {...restField}
                          name={[name, "faq_answer"]}
                          label={
                            <span
                              className="font-semibold"
                              style={{ color: "rgba(0, 0, 0, 0.88)" }}
                            >
                              Answer
                            </span>
                          }
                          rules={[
                            {
                              required: true,
                              message: "Please enter answer",
                            },
                          ]}
                        >
                          {/* <TextArea
                            placeholder="Enter answer in this language"
                            rows={4}
                            showCount
                            className="rounded-lg"
                          /> */}
                          <ReactQuillEditor
                      placeholder="Enter answer in this language"
                      Height={400}
                    />
                        </Form.Item>
                      </div>
                    ))}
                    <Button
                      type="dashed"
                      onClick={() => add({ faq_question: "", faq_answer: "" })}
                      className="w-full rounded-lg"
                      size="large"
                    >
                      Add Language
                    </Button>
                  </>
                )}
              </Form.List>
            </div>

            {/* Action Buttons */}
            <div
              className="flex justify-end gap-4 pt-6 border-t"
              style={{ borderColor: "#d9d9d9" }}
            >
              <Space size="middle">
                <Button
                  onClick={handleReset}
                  icon={<ReloadOutlined />}
                  size="large"
                  className="px-6"
                >
                  Reset
                </Button>
                <Button
                  onClick={handleGoBack}
                  icon={<ArrowLeftOutlined />}
                  size="large"
                  className="px-6"
                >
                  Cancel
                </Button>
                <Button
                  type="primary"
                  htmlType="submit"
                  loading={loading}
                  icon={<SaveOutlined />}
                  size="large"
                  className="px-8"
                >
                  {loading ? "Updating..." : "Update FAQ"}
                </Button>
              </Space>
            </div>
          </Form>
        </Card>
      </Spin>
    </div>
  );
};

export default EditFaq;
